import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiChevronDown, FiFileText, FiTruck, FiAlertCircle, FiClipboard, FiCopy, FiNavigation, FiUpload } from 'react-icons/fi';
import api from '../../services/api';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';
import { getTrackingUrl } from '../../config/carrierTracking';
import StatusSelector from '../../components/common/StatusSelector';
import ContainerTab from './ContainerTab';
import AccountingTab from './AccountingTab';
import DocCenterTab from './DocCenterTab';
import ImportShipmentModal from '../../components/ImportShipmentModal';

const TruckDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'Basic');
  const [showTools, setShowTools] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const toolsRef = useRef(null);

  // Close tools dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setShowTools(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [formData, setFormData] = useState({
    file_no: '',
    mbl_no: '',
    hbl_no: '',
    vessel_flight_no: '',
    customer: '',
    customer_ref_no: '',
    shipper: '',
    consignee: '',
    trucker: '',
    bill_to: '',
    post_date: '',
    ship_type: 'Ocean',
    status: 'Pending',
    etd: '',
    port_of_discharge: '',
    eta: '',
    port_of_loading: '',
    final_destination: '',
    final_eta: '',
    empty_pickup: '',
    freight_pickup: '',
    delivery_to: '',
    empty_return: '',
    package_type: 'CARTON(S)',
    package_count: '',
    weight_kg: '',
    weight_lb: '',
    measurement_cbm: '',
    measurement_cft: '',
    estimated_delivery_date: '',
    delivered_date: '',
    delivered: false,
    memo: '',
  });

  useEffect(() => {
    loadShipment();
  }, [id]);

  const loadShipment = async () => {
    try {
      const response = await api.get(`/truck/${id}`);
      const data = response.data;
      setShipment(data);
      setFormData({
        file_no: data.file_no || '',
        mbl_no: data.mbl_no || '',
        hbl_no: data.hbl_no || '',
        vessel_flight_no: data.vessel_flight_no || '',
        customer: data.customer || '',
        customer_ref_no: data.customer_ref_no || '',
        shipper: data.shipper || '',
        consignee: data.consignee || '',
        trucker: data.trucker || '',
        bill_to: data.bill_to || '',
        post_date: data.post_date ? data.post_date.split('T')[0] : '',
        ship_type: data.ship_type || 'Ocean',
        status: data.status || 'Pending',
        etd: data.etd ? data.etd.split('T')[0] : '',
        port_of_discharge: data.port_of_discharge || '',
        eta: data.eta ? data.eta.split('T')[0] : '',
        port_of_loading: data.port_of_loading || '',
        final_destination: data.final_destination || '',
        final_eta: data.final_eta ? data.final_eta.split('T')[0] : '',
        empty_pickup: data.empty_pickup || '',
        freight_pickup: data.freight_pickup || '',
        delivery_to: data.delivery_to || '',
        empty_return: data.empty_return || '',
        package_type: data.package_type || 'CARTON(S)',
        package_count: data.package_count || '',
        weight_kg: data.weight_kg || '',
        weight_lb: data.weight_lb || '',
        measurement_cbm: data.measurement_cbm || '',
        measurement_cft: data.measurement_cft || '',
        estimated_delivery_date: data.estimated_delivery_date ? data.estimated_delivery_date.split('T')[0] : '',
        delivered_date: data.delivered_date ? data.delivered_date.split('T')[0] : '',
        delivered: data.delivered || false,
        memo: data.memo || '',
      });
    } catch (error) {
      toast.error('Failed to load shipment');
      navigate('/truck');
    } finally {
      setLoading(false);
    }
  };

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setIsDirty(true);
  };

  const setField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { file_no, ...updateData } = formData;
      await api.put(`/truck/${id}`, updateData);
      setIsDirty(false);
      toast.success('Shipment updated successfully');
      setShipment({ ...shipment, ...formData });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save shipment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  if (!shipment) {
    return <div className="empty-state"><div className="empty-state-title">Shipment not found</div></div>;
  }

  return (
    <div className="shipment-page">
      {/* Breadcrumb */}
      <div className="shipment-breadcrumb">
        <button className="breadcrumb-back-btn" onClick={() => {
          if (isDirty && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
          navigate('/truck');
        }} title="Back">
          <FiArrowLeft />
        </button>
        <span>Home</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span>Truck</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-current">{formData.file_no}</span>
      </div>

      {/* Tab Bar */}
      <div className="shipment-tabs">
        {['Basic', 'Container', 'Accounting', 'Doc Center'].map(tab => (
          <button
            key={tab}
            className={`shipment-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Header Summary Bar */}
      <div className="shipment-header-bar">
        <div className="header-left">
          <span className="header-mbl">
            <span className="mbl-label">MB/L</span>
            <span className="mbl-value">{formData.mbl_no || '-'}</span>
          </span>
          <span className="header-customer">
            <span className="customer-label">Customer</span>
            <span className="customer-value">{formData.customer || '-'}</span>
          </span>
        </div>
        <div className="header-right">
          <StatusSelector
            value={formData.status}
            options={['Pending', 'In Transit', 'Delivered', 'Cancelled']}
            onChange={(val) => { setFormData(prev => ({ ...prev, status: val })); setIsDirty(true); }}
          />
          <span className="header-route">
            {formData.port_of_loading || '-'} ({formData.etd || '-'}) &gt; {formData.port_of_discharge || '-'} ({formData.eta || '-'})
          </span>
          {formData.mbl_no && (
            <button
              className="header-tools-btn"
              style={{ background: '#2563eb', color: '#fff', marginRight: '8px' }}
              onClick={() => {
                const tracking = getTrackingUrl(formData.vessel_flight_no || formData.trucker || '', formData.mbl_no, 'truck');
                if (tracking) {
                  window.open(tracking.url, '_blank');
                } else {
                  toast.error('No tracking number available');
                }
              }}
              title="Track shipment online"
            >
              <FiNavigation size={14} style={{ marginRight: '4px' }} /> Track
            </button>
          )}
          <div ref={toolsRef} style={{ position: 'relative' }}>
            <button className="header-tools-btn" onClick={() => setShowTools(!showTools)}>
              Tools <FiChevronDown />
            </button>
            {showTools && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, zIndex: 200,
                background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '220px',
                marginTop: '4px', overflow: 'hidden',
              }}>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>Documents</div>
                <button onClick={() => { setShowTools(false); window.open(`/documents/truck/${id}/delivery-order`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiTruck size={15} style={{ color: '#2563eb' }} /> Pickup / Delivery Order
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/truck/${id}/bill-of-lading`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiClipboard size={15} style={{ color: '#059669' }} /> Bill of Lading
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/truck/${id}/arrival-notice`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiAlertCircle size={15} style={{ color: '#d97706' }} /> Arrival Notice
                </button>
                <div style={{ borderTop: '1px solid #f1f5f9', margin: '2px 0' }}></div>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Actions</div>
                <button onClick={() => { setShowTools(false); setShowUploadModal(true); }} style={toolsMenuItemStyle}>
                  <FiUpload size={15} style={{ color: '#2563eb' }} /> Upload Document (Update)
                </button>
                <button onClick={async () => {
                  setShowTools(false);
                  if (!window.confirm('Copy this shipment? A new shipment will be created with same customer, delivery info, and charges (MBL/HBL/Container left blank).')) return;
                  try {
                    const res = await api.post(`/truck/${id}/copy`);
                    toast.success(`Shipment copied! New File No: ${res.data.file_no}`);
                    navigate(`/truck/${res.data.id}`);
                  } catch (err) {
                    toast.error('Failed to copy shipment');
                  }
                }} style={toolsMenuItemStyle}>
                  <FiCopy size={15} style={{ color: '#6366f1' }} /> Copy Shipment
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Content */}
      {activeTab === 'Basic' && (
        <form onSubmit={handleSubmit} className="shipment-form">

          {/* General Info */}
          <div className="form-section-header">General Information</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>File No.</label>
              <input type="text" value={formData.file_no} disabled />
            </div>
            <div className="shipment-form-group">
              <label>Post Date</label>
              <input type="date" name="post_date" value={formData.post_date} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="Pending">Pending</option>
                <option value="In Transit">In Transit</option>
                <option value="Delivered">Delivered</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="shipment-form-group">
              <label>Ship Type</label>
              <select name="ship_type" value={formData.ship_type} onChange={handleChange}>
                <option value="Ocean">Ocean</option>
                <option value="Air">Air</option>
                <option value="Land">Land</option>
              </select>
            </div>
          </div>

          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>MB/L No.</label>
              <input type="text" name="mbl_no" value={formData.mbl_no} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>HB/L No.</label>
              <input type="text" name="hbl_no" value={formData.hbl_no} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Vessel/Flight No.</label>
              <input type="text" name="vessel_flight_no" value={formData.vessel_flight_no} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Customer Ref No.</label>
              <input type="text" name="customer_ref_no" value={formData.customer_ref_no} onChange={handleChange} />
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Parties */}
          <div className="form-section-header">Parties</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Customer</label>
              <TradePartnerAutocomplete value={formData.customer} placeholder="Search customer..."
                onChange={(val) => setField('customer', val)} />
            </div>
            <div className="shipment-form-group">
              <label>Shipper</label>
              <TradePartnerAutocomplete value={formData.shipper} tpTypes="Customer,Vendor" placeholder="Search shipper..."
                onChange={(val) => setField('shipper', val)} />
            </div>
            <div className="shipment-form-group">
              <label>Consignee</label>
              <TradePartnerAutocomplete value={formData.consignee} tpTypes="Customer,Vendor" placeholder="Search consignee..."
                onChange={(val) => setField('consignee', val)} />
            </div>
            <div className="shipment-form-group">
              <label>Bill To</label>
              <TradePartnerAutocomplete value={formData.bill_to} placeholder="Search bill to..."
                onChange={(val) => setField('bill_to', val)} />
            </div>
          </div>

          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Trucker</label>
              <TradePartnerAutocomplete value={formData.trucker} tpTypes="Trucker" placeholder="Search trucker..."
                onChange={(val) => setField('trucker', val)} />
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Route Info */}
          <div className="form-section-header">Route Information</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>ETD</label>
              <input type="date" name="etd" value={formData.etd} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Port of Loading</label>
              <TradePartnerAutocomplete value={formData.port_of_loading} placeholder="Port of loading..."
                onChange={(val) => setField('port_of_loading', val)} />
            </div>
            <div className="shipment-form-group">
              <label>ETA</label>
              <input type="date" name="eta" value={formData.eta} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Port of Discharge</label>
              <TradePartnerAutocomplete value={formData.port_of_discharge} placeholder="Port of discharge..."
                onChange={(val) => setField('port_of_discharge', val)} />
            </div>
          </div>

          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Final Destination</label>
              <TradePartnerAutocomplete value={formData.final_destination} placeholder="Final destination..."
                onChange={(val) => setField('final_destination', val)} />
            </div>
            <div className="shipment-form-group">
              <label>Final ETA</label>
              <input type="date" name="final_eta" value={formData.final_eta} onChange={handleChange} />
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Delivery Info */}
          <div className="form-section-header">Delivery</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Empty Pickup</label>
              <TradePartnerAutocomplete value={formData.empty_pickup} placeholder="Empty pickup..."
                onChange={(val) => setField('empty_pickup', val)} />
            </div>
            <div className="shipment-form-group">
              <label>Freight Pickup</label>
              <TradePartnerAutocomplete value={formData.freight_pickup} placeholder="Freight pickup..."
                onChange={(val) => setField('freight_pickup', val)} />
            </div>
            <div className="shipment-form-group">
              <label>Delivery To</label>
              <TradePartnerAutocomplete value={formData.delivery_to} placeholder="Delivery to..."
                onChange={(val) => setField('delivery_to', val)} />
            </div>
            <div className="shipment-form-group">
              <label>Empty Return</label>
              <TradePartnerAutocomplete value={formData.empty_return} placeholder="Empty return..."
                onChange={(val) => setField('empty_return', val)} />
            </div>
          </div>

          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Estimated Delivery</label>
              <input type="date" name="estimated_delivery_date" value={formData.estimated_delivery_date} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Delivered</label>
              <div className="delivered-row">
                <input type="checkbox" name="delivered" checked={formData.delivered} onChange={handleChange} />
                <input type="date" name="delivered_date" value={formData.delivered_date} onChange={handleChange} />
              </div>
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Cargo Details */}
          <div className="form-section-header">Cargo Details</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Package</label>
              <div className="package-row">
                <select name="package_type" value={formData.package_type} onChange={handleChange}>
                  <option value="CARTON(S)">CARTON(S)</option>
                  <option value="PALLET(S)">PALLET(S)</option>
                  <option value="BOX(ES)">BOX(ES)</option>
                  <option value="CASE(S)">CASE(S)</option>
                  <option value="PIECE(S)">PIECE(S)</option>
                </select>
                <input type="number" name="package_count" value={formData.package_count} onChange={handleChange} placeholder="Qty" />
              </div>
            </div>
            <div className="shipment-form-group">
              <label>Weight</label>
              <div className="weight-row">
                <input type="number" step="0.01" name="weight_kg" value={formData.weight_kg} onChange={handleChange} placeholder="0.00" />
                <span className="unit">KG</span>
                <input type="number" step="0.01" name="weight_lb" value={formData.weight_lb} onChange={handleChange} placeholder="0.00" />
                <span className="unit">LB</span>
              </div>
            </div>
            <div className="shipment-form-group">
              <label>Measurement</label>
              <div className="measurement-row">
                <input type="number" step="0.01" name="measurement_cbm" value={formData.measurement_cbm} onChange={handleChange} placeholder="0.00" />
                <span className="unit">CBM</span>
                <input type="number" step="0.01" name="measurement_cft" value={formData.measurement_cft} onChange={handleChange} placeholder="0.00" />
                <span className="unit">CFT</span>
              </div>
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Memo */}
          <div className="form-section-header">Memo</div>
          <div className="shipment-form-group">
            <textarea name="memo" value={formData.memo} onChange={handleChange} className="shipment-memo" placeholder="Add notes here..." />
          </div>

          {/* Actions */}
          <div className="shipment-form-actions">
            <button type="button" className="btn-cancel" onClick={() => {
              if (isDirty && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
              navigate('/truck');
            }}>
              Cancel
            </button>
            <button type="submit" className="shipment-save-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}

      {/* Container Tab */}
      {activeTab === 'Container' && (
        <ContainerTab shipmentId={id} shipmentType="truck" />
      )}

      {/* Accounting Tab */}
      {activeTab === 'Accounting' && (
        <AccountingTab shipmentId={id} shipmentType="truck" />
      )}

      {/* Doc Center Tab */}
      {activeTab === 'Doc Center' && (
        <DocCenterTab shipmentId={id} shipmentType="truck" />
      )}

      {/* Upload Document Modal for updating existing shipment */}
      {showUploadModal && id && (
        <ImportShipmentModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          defaultType="truck"
          existingShipment={{ id, type: 'truck' }}
          onUpdated={() => {
            loadShipment();
          }}
        />
      )}
    </div>
  );
};

const toolsMenuItemStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  width: '100%', padding: '10px 14px', border: 'none',
  background: 'transparent', cursor: 'pointer', fontSize: '13px',
  fontWeight: 500, color: '#334155', textAlign: 'left',
  transition: 'background 0.15s',
};

export default TruckDetail;
